import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  RadioGroup,
  Radio,
  Button,
  IconButton,
} from '@mui/material';
import { AccessTime, Delete } from '@mui/icons-material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { StyledFormControlLabel } from 'routes/Main/style';
import GridItem from '../../components/GridItem';
import MuiTextField from 'components/Form/MuiTextField';
import MuiTable from 'components/MuiTable';

import { Ti18nId } from 'hooks/useI18n';
import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';
import { IFLACC } from 'apis/survey/type';
import { formatStringToDate } from 'utils/formatting';

import theme from 'styles/theme';

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
  onRequired: (id: Ti18nId) => void;
  onSuccess: (message: string) => void;
}

const radioId = ['face', 'legs', 'activity', 'cry', 'consolability'];

const FlaccContents = (props: Props) => {
  const { disabled, watch, setValue, onRequired, onSuccess, register } = props;
  const flaccList: IFLACC[] = watch('flacc_survey');

  const [date, setDate] = useState('');
  const [checkTime, setCheckTime] = useState(null);
  const [sumValue, setSumValue] = useState(0);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>, v: string) => {
    setValue(e.target.name, e.target.value);
    setSumValue(
      radioId.reduce((acc, cur) => {
        const value = Number(watch(cur));
        return value ? acc + value : acc;
      }, 0)
    );
  };

  const columns = [
    { fieldId: 'date', label: 'Ngày', sx: { width: 200 } },
    { fieldId: 'time', label: 'Giờ', sx: { width: 200 } },
    { fieldId: 'sum', label: 'Mức độ' },
    { fieldId: 'action', label: '', sx: { width: 100 } },
  ];

  const onAddRow = () => {
    const request = { date, time: checkTime, sum: sumValue };

    if (checkTime === null) return onRequired('REQUIRED.NONCHOICE');
    for (let i = 0; i < radioId.length; i++) {
      if (watch(radioId[i]) === undefined)
        return onRequired('REQUIRED.NONCHOICE');
    }

    onSuccess('Đã được thêm vào.');
    setValue('flacc_survey', flaccList ? [...flaccList, request] : [request]);
    setValue('flacc_scale_date', '');
    setDate('');
    setCheckTime(null);
  };

  const inputRow = {
    id: 'add-nrs',
    date: (
      <MuiTextField
        type="date"
        required={false}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        {...register('flacc_scale_date', {
          onChange: e => setDate(e.target.value),
        })}
      />
    ),
    time: (
      <MobileTimePicker
        value={checkTime}
        onChange={setCheckTime}
        renderInput={params => (
          <MuiTextField
            {...params}
            required={false}
            placeholder="00:00"
            InputProps={{ endAdornment: <AccessTime /> }}
          />
        )}
      />
    ),
    sum: (
      <>
        <MuiTextField value={sumValue} required={true} disabled />
      </>
    ),
    action: (
      <Button
        variant="contained"
        size="small"
        onClick={onAddRow}
        sx={{ backgroundColor: '#2D73DC', color: '#E7EFFF' }}
      >
        Thêm
      </Button>
    ),
  };

  const onDeleteRow = (index: number) => {
    setValue(
      'flacc_survey',
      flaccList.filter((_, i) => i !== index)
    );
  };

  const displayRows = flaccList
    ? flaccList.map((item, i) => ({
        ...item,
        id: i,
        time: formatStringToDate(item.time, 'hh:mm a'),
        action: (
          <IconButton
            size="small"
            onClick={() => onDeleteRow(i)}
            sx={{ display: disabled ? 'none' : 'block' }}
          >
            <Delete />
          </IconButton>
        ),
      }))
    : [];
  const tableRow = disabled ? displayRows : [inputRow, ...displayRows];

  return (
    <>
      <Box sx={{ width: '100%', marginTop: '40px', marginBottom: '12px' }}>
        <MuiTable columns={columns} rows={tableRow} />
      </Box>
      <Box sx={{ width: '1200px', margin: '24px auto' }}>
        <Grid container spacing={1}>
          <GridItem bgColor="#0000001F" text="" />
          <GridItem bgColor="#0000001F" text="0" />
          <GridItem bgColor="#0000001F" text="1" />
          <GridItem bgColor="#0000001F" text="2" />

          <GridItem bgColor="#0000001F" text="Mặt" />
          <RadioGroup
            name={'face'}
            row
            sx={{
              width: '75%',
              display: 'flex',
              justifyContent: 'space-around',
            }}
            onChange={onChange}
          >
            <StyledFormControlLabel
              value={0}
              label={'Cười hoặc không có biểu cảm  đặc biệt '}
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={1}
              label={
                'Thi thoảng nhăn nhó hoặc nhíu mày, thu mình hoặc thờ ơ, tỏ ra buồn bã, lo lắng'
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={2}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Thường xuyên đến liên tục nhíu mày, nghiến răng, run cằm,
                  khuôn mặt trông đau buồn; thể hiện sự sợ hãi hay hoảng sợ'
                </Typography>
              }
              control={<Radio />}
            />
          </RadioGroup>

          <GridItem bgColor="#0000001F" text="Hai chân" />
          <RadioGroup
            name={'legs'}
            row
            sx={{
              width: '75%',
              display: 'flex',
              justifyContent: 'space-around',
            }}
            onChange={onChange}
          >
            <StyledFormControlLabel
              value={0}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Tư thế bình thường hoặc thoải mái; độ săn chắc của cơ bắp và
                  vận động tay chân bình thường
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={1}
              label={'Bứt rứt, không yên, căng thẳng ; thi thoảng run rẩy'}
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={2}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Đạp hoặc co rút chân; co cứng cơ tăng đáng kể; liên tục run
                  rẩy hoặc đá chân
                </Typography>
              }
              control={<Radio />}
            />
          </RadioGroup>

          <GridItem bgColor="#0000001F" text="Hoạt động" />
          <RadioGroup
            name={'activity'}
            row
            sx={{
              width: '75%',
              display: 'flex',
              justifyContent: 'space-around',
            }}
            onChange={onChange}
          >
            <StyledFormControlLabel
              value={0}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Nằm yên, tư thế bình thường, cử động dễ dàngđộng, nhịp thở (hô
                  hấp) đều đặn bình thường
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={1}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Nằm không yên, ngoáy ngó, căng thẳng; hơi bị kích thích, thở
                  nông, giảm thở (hô hấp) vì đau, thỉnh thoảng thở dài
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={2}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '14px' }}>
                  Cong, gồng cứng người lại hoặc giật, kích động mạnh; đập đầu,
                  run rẩy (không phải run do sốt); nín thở, thở hổn hển hoặc thở
                  gấp; giảm thở rất nhiều vì đau
                </Typography>
              }
              control={<Radio />}
            />
          </RadioGroup>

          <GridItem bgColor="#0000001F" text="Khóc" />
          <RadioGroup
            name={'cry'}
            row
            sx={{
              width: '75%',
              display: 'flex',
              justifyContent: 'space-around',
            }}
            onChange={onChange}
          >
            <StyledFormControlLabel
              value={0}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Không khóc ( lúc thức và ngủ)
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={1}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Kêu rên rỉ hoặc khóc thút thít, thi thoảng kêu đau, thi thoảng
                  kêu ầm lên hoặc càu nhàu
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={2}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '14px' }}>
                  Khóc không đứt, kêu thét lên hoặc khóc nức nở, thường xuyên
                  kêu đau, các cơn khóc ầm ĩ tái diễn, liên tục càu nhàu
                </Typography>
              }
              control={<Radio />}
            />
          </RadioGroup>

          <GridItem
            bgColor="#0000001F"
            text="ConsoĐáp ứng khi được dỗ dànhability"
          />
          <RadioGroup
            name={'consolability'}
            row
            sx={{
              width: '75%',
              display: 'flex',
              justifyContent: 'space-around',
            }}
            onChange={onChange}
          >
            <StyledFormControlLabel
              value={0}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Thoải máiT, thư giãn, không cần an ủi
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={1}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '16px' }}>
                  Thấy an tâm khi thi thoảng được vỗ về ôm ấp, hoặc nói chuyện;
                  có thể làm cho quên đau
                </Typography>
              }
              control={<Radio />}
            />
            <StyledFormControlLabel
              value={2}
              label={
                <Typography sx={{ fontsize: '12px', lineHeight: '14px' }}>
                  Khó dỗ dành hoặc vỗ về; từ chối người chăm sóc; chống lại sự
                  chăm sóc hoặc các biện pháp can thiệp làm dịu
                </Typography>
              }
              control={<Radio />}
            />
          </RadioGroup>
        </Grid>
        <Grid item xs={12}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-end'}
            sx={{ marginTop: '20px' }}
          >
            <Typography
              gutterBottom
              minWidth={115}
              fontWeight={700}
              variant="subtitle1"
            >
              Tổng: {sumValue}
            </Typography>
            <Typography
              minWidth={115}
              variant="caption"
              sx={{ color: `${theme.palette.primary.dark}` }}
            >
              <Typography variant="inherit">
                <Box component={'strong'} mr={0.5}>
                  0 điểm:
                </Box>
                Thư giãn và thoải mái
              </Typography>
              <Typography variant="inherit">
                <Box component={'strong'} mr={0.5}>
                  1~3 điểm:
                </Box>
                Cảm thấy khó chịu nhẹ
              </Typography>
              <Typography variant="inherit">
                <Box component={'strong'} mr={0.5}>
                  4~6 điểm:
                </Box>
                đau vừa phải
              </Typography>
              <Typography variant="inherit">
                <Box component={'strong'} mr={0.5}>
                  7~10 điểm:
                </Box>
                đau hoặc rất khó chịu
              </Typography>
            </Typography>
          </Box>
        </Grid>
      </Box>
    </>
  );
};

export default FlaccContents;
